import { Typography } from '@design-system/components/typography/typography';
import { rgb } from '@design-system/tokens';
import Spinner from '@client/components/Spinner';
import { ReactComponent as LogoSvg } from '../../../assets/logo.svg';
import { PHONE_COUNTRY_OPTIONS } from '../model/auth.phone-options.model';
import {
  getRegisterStepProgress,
  isProfileFlowStep,
} from '../model/registration.model';
import {
  authVmCopies,
  type RegisterViewModel,
} from '../view-model/register.types';
import { testimonialsData } from './testimonials.data';
import * as styles from './register.view.css';
import { isAuthDesktopViewport } from './viewport';
import { AuthLayout } from './components/AuthLayout';
import { AuthProgress } from './components/AuthProgress';
import { TestimonialsCarousel } from './components/TestimonialsCarousel';
import { EmailStep } from './steps/EmailStep';
import { ProfileFlowStep } from './steps/ProfileFlowStep';

export const RegisterView = (value: RegisterViewModel) => {
  const { state, dispatch } = value;
  const handleRestartFlow = () => dispatch('restartFlow');
  const showRegistrationWaiting =
    state.step === 'code' && state.submission.type === 'registering-account';
  const shouldAutoFocusInput = isAuthDesktopViewport();

  const testimonialsContent = (
    <section
      className={styles.testimonialsPanel}
      aria-label={authVmCopies.common.creatorTestimonialsAriaLabel}
    >
      <TestimonialsCarousel testimonials={testimonialsData} />
    </section>
  );

  const progressConfig = getRegisterStepProgress(state.step);

  const footerProgress = progressConfig ? (
    <div className={styles.progressFooter}>
      <AuthProgress
        currentStep={progressConfig.currentStep}
        totalSteps={progressConfig.totalSteps}
        showBack={progressConfig.showBack}
        onBack={progressConfig.showBack ? () => dispatch('back') : undefined}
      />
    </div>
  ) : null;

  const sharedProps = {
    canContinue: state.canContinue,
    shouldAutoFocusInput,
    dispatch,
  };

  const renderRegistrationWaitingStep = () => {
    return (
      <AuthLayout
        withBox={false}
        className={styles.singleColumnBox}
        header={<LogoSvg className={styles.brandLogo} />}
        onLogoClick={handleRestartFlow}
        bodyClassName={styles.waitingBody}
        contentClassName={styles.waitingContent}
        content={
          <div className={styles.waitingInner}>
            <Spinner color={rgb('P-80')} size={24} duration={900} />
            <Typography
              variant="body"
              size="medium"
              className={styles.waitingMessage}
            >
              Please wait while we register your account...
            </Typography>
          </div>
        }
      />
    );
  };

  const activeMainContent = showRegistrationWaiting ? (
    renderRegistrationWaitingStep()
  ) : isProfileFlowStep(state.step) ? (
    <ProfileFlowStep
      step={state.step}
      {...sharedProps}
      values={state.values}
      formState={value.toStepFormState(state.step)}
      options={PHONE_COUNTRY_OPTIONS}
      testimonialsContent={testimonialsContent}
      footerProgress={footerProgress}
    />
  ) : (
    <EmailStep
      {...sharedProps}
      values={{ email: state.values.email }}
      formState={value.toStepFormState('email')}
      testimonialsContent={testimonialsContent}
    />
  );

  return <div className={styles.stepTransition}>{activeMainContent}</div>;
};
